import { KeyboardArrowDownRounded, ManageSearchRounded } from "@mui/icons-material";
import { Button, Divider, Popover, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useGetTeamsTree } from "../http";
import { ITreeItem } from "../models";
import { getTreeViewSelectedItemsLabel, TreeView } from "./TreeView";

const BUTTON_TITLE_PLACEHOLDER = "Select Team";

interface IProps {
  selectedTeamGuids: string[];
  onTeamSelected: (selectedTeamGuids: string[]) => void;
  onClearClicked: () => void;
}

const TeamPicker = (props: IProps) => {
  const { data: teams, isLoading } = useGetTeamsTree();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [buttonTitle, setButtonTitle] = useState(BUTTON_TITLE_PLACEHOLDER);
  const [tooltipTitle, setTooltipTitle] = useState<React.ReactNode | null>(null);

  useEffect(() => {
    if (teams != null) {
      const selectedTeams = teams?.filter((x) => props.selectedTeamGuids.includes(x.itemId));
      const label = getTreeViewSelectedItemsLabel(selectedTeams);

      setButtonAndTooltipTitles(label, selectedTeams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedTeamGuids, teams]);

  function setButtonAndTooltipTitles(label: string, selectedTeams: ITreeItem[]) {
    if (selectedTeams.length === 1) {
      setButtonTitle(selectedTeams[0].label);
      setTooltipTitle(null);
    } else if (selectedTeams.length > 1) {
      setButtonTitle(label ?? "-");
      setTooltipTitle(
        <>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Selected</Typography>
          {selectedTeams
            .sort((a, b) => a.label.localeCompare(b.label))
            .map((x, i) => (
              <Typography key={i} sx={{ display: "inline-block", fontSize: 13, marginRight: "2px" }}>
                {x.label}
                {i < props.selectedTeamGuids.length - 1 && ", "}
              </Typography>
            ))}
        </>
      );
    } else {
      setButtonTitle(BUTTON_TITLE_PLACEHOLDER);
      setTooltipTitle(null);
    }
  }

  function handleTeamSelected(selectedTeams: ITreeItem[]) {
    props.onTeamSelected(selectedTeams.map((x) => x.itemId));
  }

  function handleClearClicked() {
    setButtonTitle(BUTTON_TITLE_PLACEHOLDER);
    setTooltipTitle(null);

    props.onClearClicked();
  }

  return (
    <>
      <Tooltip
        title={tooltipTitle}
        placement="bottom-start"
        slotProps={{
          tooltip: {
            sx: {
              maxHeight: "400px",
              overflowY: "auto",
            },
          },
        }}
      >
        <Button
          variant="tertiary"
          startIcon={<ManageSearchRounded />}
          endIcon={<KeyboardArrowDownRounded />}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          {buttonTitle}
        </Button>
      </Tooltip>

      <Popover
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Stack spacing={0.5} sx={{ maxHeight: "400px", overflow: "auto", pt: 1, pb: 0.5 }}>
          {isLoading && (
            <Stack>
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} width={240} />
              ))}
            </Stack>
          )}

          {!isLoading && teams && (
            <TreeView items={teams} selectedItemIds={props.selectedTeamGuids} onItemSelected={handleTeamSelected} />
          )}
        </Stack>
        <Stack spacing={0.5} sx={{ mx: 1, mb: 0.5 }}>
          <Divider variant="dashed" sx={{ color: "#c9c9c9", mt: "0 !important", mx: 1 }} />
          <Button variant="tertiary" sx={{ alignSelf: "start" }} onClick={handleClearClicked}>
            Clear
          </Button>
        </Stack>
      </Popover>
    </>
  );
};

export { TeamPicker };
